import React, { useState, useEffect, useRef } from 'react';
import { findHighlightRanges } from './highlight';
import { ADVANCE_MS_EXAMPLE, ADVANCE_MS_PLAIN, DIMMED_OPTION, GOOD_OPTION, BAD_OPTION, pickDistractors, clozeDistractors } from './quizUtils';

// ACTIF : WordReverse et WordBuild (plus bas) sont les exercices de PRODUCTION
// pilotés par la force SRS (chantier C). Ils portent sur le mot courant et
// rapportent un résultat correct/faux à QuizGame.
//
// DORMANT : les composants ci-dessous (ReverseGame, ClozeGame, AnagramGame,
// MatchGame, AudioGame + pickBonusType) étaient l'ancien « bonus tous les 7 bons
// d'affilée ». Ce déclencheur ALÉATOIRE a été RETIRÉ (recadrage v2 : zéro exercice
// gratuit, chaque exercice doit servir un but). On les garde comme matière première
// à repurposer proprement : ClozeGame/AudioGame → modalités « comprendre »/« entendre »,
// l'exercice « réplique », etc. Ils ne sont plus câblés nulle part pour l'instant.
//
// Audio : `play` = lecture demandée par un clic (elle joue même son coupé),
// `playAuto` = lecture déclenchée par le jeu (elle obéit au réglage du son).
//
// Audio : `play` = lecture demandée par un clic (elle joue même son coupé),
// `playAuto` = lecture déclenchée par le jeu (elle obéit au réglage du son).

const audioUrl = (id, type) => `/static/audio/${id}_${type}.mp3`;
const shuffle = (arr) => arr.slice().sort(() => Math.random() - 0.5);
const REVEAL_MS = 1500; // fin des jeux de construction (paires, anagramme)
const AUTO_MS = 700;    // bonus réussi : courte avance auto, sans feedback bloquant

const OPT_BASE =
  'px-4 py-4 rounded-xl text-sm font-medium border transition-all break-words hyphens-auto';

// Consigne de l'exercice : même discrétion que les invitations de la carte QCM
// (11 px, slate-600). En text-xs/slate-400 elle pesait plus lourd que la question.
const Hint = ({ children }) => (
  <p className="text-[11px] text-slate-600 text-center">{children}</p>
);

// Bouton d'écoute : même icône SVG que le quiz principal, pour une UI unifiée.
const PlayButton = ({ onClick, title, className = '' }) => (
  <button
    onClick={onClick}
    className={`shrink-0 opacity-30 hover:opacity-80 transition-opacity ${className}`}
    title={title}
  >
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-blue-300">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
    </svg>
  </button>
);

// Bouton « suivant » affiché après une MAUVAISE réponse à un jeu à choix :
// l'utilisateur reste sur le feedback et passe manuellement à la suite.
const NextButton = ({ onDone, label }) => (
  <button
    onClick={onDone}
    className="mx-auto mt-1 px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors"
  >
    {label}
  </button>
);

// Classe d'une option de QCM selon l'état (neutre / bonne / mauvaise / éteinte).
// Aligné sur les boutons du quiz principal (getButtonClass) pour une UI unifiée.
const optClass = (state) => {
  if (state === 'idle')
    return `${OPT_BASE} bg-slate-700 text-white border-slate-600 hover:bg-blue-600 hover:border-blue-500 cursor-pointer active:scale-95`;
  if (state === 'good') return `${OPT_BASE} ${GOOD_OPTION}`;
  if (state === 'bad') return `${OPT_BASE} ${BAD_OPTION}`;
  // Leurre devenu inutile : il s'efface mais garde sa case (même règle que le QCM).
  return `${OPT_BASE} ${DIMMED_OPTION}`;
};

// Chrono expiré sur un exercice de production. Même contrat que le timeout du QCM :
// on RÉVÈLE la réponse et on enchaîne, sans rien écrire dans le SRS — d'où
// `onDone(null)`, qui est déjà la convention « ne rien enregistrer ». Le chrono
// donne le tempo, il ne témoigne de rien.
const useExpiry = (expired, answered, onSettled, onDone, durationMs, reveal) => {
  const fired = useRef(false);
  const timerRef = useRef(null);
  useEffect(() => {
    if (!expired || answered || fired.current) return;
    fired.current = true;
    reveal();
    onSettled?.(durationMs);
    timerRef.current = setTimeout(() => onDone(null), durationMs);
    // PAS de `return () => clearTimeout(...)` ici. `reveal()` change l'état que
    // `answered` observe (le résultat de l'exercice), donc l'effet se relance
    // aussitôt : le nettoyage annulait le timer d'avance qu'on venait d'armer, et
    // la relance ressortait tout de suite par `fired.current`. Résultat : plus
    // personne pour appeler onDone → la carte restait figée sur la révélation,
    // définitivement. C'était le gel observé sur l'écran de reconstitution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired, answered]);
  // Le timer ne meurt qu'avec le composant.
  useEffect(() => () => clearTimeout(timerRef.current), []);
};

// Lance onDone() si le jeu n'a pas pu être construit (pool insuffisant) → on enchaîne.
const useSkipIfEmpty = (data, onDone) => {
  const done = useRef(false);
  useEffect(() => {
    if (!data && !done.current) {
      done.current = true;
      onDone();
    }
  }, [data, onDone]);
};

// ── Sens inverse : traduction affichée → choisir le mot russe ────────────────
function ReverseGame({ pool, lang, onDone }) {
  const [data] = useState(() => {
    const words = pool.filter((w) => w.russe && w[lang.wordField]);
    if (words.length < 4) return null;
    const picked = shuffle(words).slice(0, 4);
    return { answer: picked[0], options: shuffle(picked) };
  });
  const [selected, setSelected] = useState(null);
  useSkipIfEmpty(data, onDone);
  if (!data) return null;

  const choose = (opt) => {
    if (selected) return;
    setSelected(opt);
    if (opt.id === data.answer.id) setTimeout(onDone, AUTO_MS);
  };

  return (
    <div className="flex flex-col gap-5 py-2">
      <Hint>{lang.ui.bonus.reverse}</Hint>
      <p className="text-center text-2xl font-semibold text-white break-words">
        {data.answer[lang.wordField]}
      </p>
      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt) => {
          const state = !selected
            ? 'idle'
            : opt.id === data.answer.id
            ? 'good'
            : opt.id === selected.id
            ? 'bad'
            : 'off';
          return (
            <button
              key={opt.id}
              lang="ru"
              onClick={() => choose(opt)}
              disabled={!!selected}
              className={`${optClass(state)} text-center`}
            >
              {opt.russe}
            </button>
          );
        })}
      </div>
      {selected && selected.id !== data.answer.id && (
        <NextButton onDone={onDone} label={lang.ui.next} />
      )}
    </div>
  );
}

// ── Texte à trou : phrase d'exemple avec le mot masqué → retrouver le mot ────
function ClozeGame({ pool, lang, play, onDone }) {
  const [data] = useState(() => {
    for (const w of shuffle(pool.filter((x) => x.russe && x[lang.exampleRuField]))) {
      const sentence = w[lang.exampleRuField];
      const ranges = findHighlightRanges(sentence, w.russe);
      if (!ranges.length) continue;
      const [start, end] = ranges[0];
      const distractors = shuffle(pool.filter((x) => x.id !== w.id && x.russe)).slice(0, 3);
      if (distractors.length < 3) continue;
      return {
        word: w,
        before: sentence.slice(0, start),
        form: sentence.slice(start, end),
        after: sentence.slice(end),
        options: shuffle([w, ...distractors]),
      };
    }
    return null;
  });
  const [selected, setSelected] = useState(null);
  useSkipIfEmpty(data, onDone);
  if (!data) return null;

  const solved = selected && selected.id === data.word.id;
  const choose = (opt) => {
    if (selected) return;
    setSelected(opt);
    // Correct → courte avance auto. Faux → on reste sur le feedback (réécoute,
    // traduction) jusqu'au clic sur « suivant ».
    if (opt.id === data.word.id) setTimeout(onDone, AUTO_MS);
  };

  return (
    <div className="flex flex-col gap-5 py-2">
      <Hint>{lang.ui.bonus.cloze}</Hint>
      <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-4 text-center">
        <p lang="ru" className="text-base text-blue-100 leading-relaxed break-words">
          {data.before}
          {selected ? (
            <strong className={solved ? 'text-green-300 font-bold' : 'text-red-300 font-bold'}>
              {data.form}
            </strong>
          ) : (
            <span className="text-blue-300 font-semibold tracking-widest">·····</span>
          )}
          {data.after}
        </p>
        {selected && data.word[lang.exampleLangField] && (
          <p className="text-xs text-slate-400 mt-2 break-words">{data.word[lang.exampleLangField]}</p>
        )}
        {selected && (
          <div className="mt-2 flex justify-center">
            <PlayButton onClick={() => play(audioUrl(data.word.id, lang.audioEx))} title={lang.ui.listen} />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt) => {
          const state = !selected
            ? 'idle'
            : opt.id === data.word.id
            ? 'good'
            : opt.id === selected.id
            ? 'bad'
            : 'off';
          return (
            <button
              key={opt.id}
              lang="ru"
              onClick={() => choose(opt)}
              disabled={!!selected}
              className={`${optClass(state)} text-center`}
            >
              {opt.russe}
            </button>
          );
        })}
      </div>
      {selected && !solved && <NextButton onDone={onDone} label={lang.ui.next} />}
    </div>
  );
}

// ── Anagramme : remettre les lettres du mot russe dans l'ordre ───────────────
function AnagramGame({ pool, lang, play, playAuto, onDone }) {
  const [data] = useState(() => {
    const words = pool.filter((w) => {
      if (!w.russe || !w[lang.wordField] || /\s/.test(w.russe)) return false;
      const n = [...w.russe].length;
      return n >= 3 && n <= 8;
    });
    if (!words.length) return null;
    const w = shuffle(words)[0];
    const target = w.russe.toLowerCase();
    const chars = [...target].map((ch, i) => ({ id: i, ch }));
    let tiles;
    let tries = 0;
    do {
      tiles = shuffle(chars);
      tries += 1;
    } while (tries < 8 && tiles.map((t) => t.ch).join('') === target);
    return { word: w, target, tiles };
  });
  const [placed, setPlaced] = useState([]); // ids de tuiles, dans l'ordre posé
  const [wrong, setWrong] = useState(false);
  const solvedRef = useRef(false);
  useSkipIfEmpty(data, onDone);
  if (!data) return null;

  const tileById = (id) => data.tiles.find((t) => t.id === id);
  const built = placed.map((id) => tileById(id).ch).join('');
  const solved = built === data.target;
  const available = data.tiles.filter((t) => !placed.includes(t.id));

  const placeTile = (id) => {
    if (solvedRef.current || placed.includes(id)) return;
    const next = [...placed, id];
    setPlaced(next);
    setWrong(false);
    if (next.length === data.target.length) {
      if (next.map((i) => tileById(i).ch).join('') === data.target) {
        solvedRef.current = true;
        playAuto(audioUrl(data.word.id, 'word')); // lecture non demandée : son coupé = silence
        setTimeout(onDone, REVEAL_MS);
      } else {
        setWrong(true);
        setTimeout(() => {
          setPlaced([]);
          setWrong(false);
        }, 700);
      }
    }
  };
  const removeAt = (idx) => {
    if (solvedRef.current) return;
    setPlaced(placed.filter((_, i) => i !== idx));
    setWrong(false);
  };

  const slotCls = solved
    ? 'border-green-500 bg-green-900/40 text-green-300'
    : wrong
    ? 'border-red-500 bg-red-900/40 text-red-300'
    : 'border-blue-500/50 bg-blue-900/30 text-blue-200';

  return (
    <div className="flex flex-col gap-5 py-2">
      <Hint>{lang.ui.bonus.anagram}</Hint>
      <p className="flex items-center justify-center gap-1 text-center text-sm text-slate-300">
        {data.word[lang.wordField]}
        <PlayButton onClick={() => play(audioUrl(data.word.id, 'word'))} title={lang.ui.listen} />
      </p>

      <div lang="ru" className={`flex flex-wrap justify-center gap-1.5 min-h-[3rem] ${wrong ? 'animate-pulse' : ''}`}>
        {placed.length === 0 ? (
          <span className="text-slate-600 text-sm self-center">…</span>
        ) : (
          placed.map((id, idx) => (
            <button
              key={id}
              onClick={() => removeAt(idx)}
              className={`w-10 h-12 rounded-lg text-xl font-bold border ${slotCls}`}
            >
              {tileById(id).ch}
            </button>
          ))
        )}
      </div>

      <div lang="ru" className="flex flex-wrap justify-center gap-1.5">
        {available.map((t) => (
          <button
            key={t.id}
            onClick={() => placeTile(t.id)}
            disabled={solved}
            className="w-10 h-12 rounded-lg text-xl font-bold border border-slate-600 bg-slate-700 text-white hover:border-blue-500 hover:bg-blue-600 active:scale-95 transition-colors"
          >
            {t.ch}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Paires : relier 4 mots russes à leurs traductions en tapant ──────────────
function MatchGame({ pool, lang, onDone }) {
  const [data] = useState(() => {
    const words = pool.filter((w) => w.russe && w[lang.wordField]);
    if (words.length < 4) return null;
    const picked = shuffle(words).slice(0, 4);
    return { pairs: picked, left: shuffle(picked), right: shuffle(picked) };
  });
  const [selLeft, setSelLeft] = useState(null);
  const [matched, setMatched] = useState([]);
  const [wrongRight, setWrongRight] = useState(null);
  const doneRef = useRef(false);
  useSkipIfEmpty(data, onDone);
  if (!data) return null;

  const tapLeft = (id) => {
    if (matched.includes(id)) return;
    setSelLeft(id);
  };
  const tapRight = (id) => {
    if (matched.includes(id) || selLeft == null) return;
    if (id === selLeft) {
      const next = [...matched, id];
      setMatched(next);
      setSelLeft(null);
      if (next.length === data.pairs.length && !doneRef.current) {
        doneRef.current = true;
        setTimeout(onDone, REVEAL_MS);
      }
    } else {
      setWrongRight(id);
      setSelLeft(null);
      setTimeout(() => setWrongRight(null), 500);
    }
  };

  const cell = (extra) => `${OPT_BASE} text-center ${extra}`;
  return (
    <div className="flex flex-col gap-5 py-2">
      <Hint>{lang.ui.bonus.match}</Hint>
      <div className="grid grid-cols-2 gap-3">
        <div lang="ru" className="flex flex-col gap-2">
          {data.left.map((w) => {
            const done = matched.includes(w.id);
            const sel = selLeft === w.id;
            return (
              <button
                key={w.id}
                onClick={() => tapLeft(w.id)}
                disabled={done}
                className={cell(
                  done
                    ? 'border-green-500 bg-green-900/30 text-green-300'
                    : sel
                    ? 'border-blue-400 bg-blue-600 text-white'
                    : 'border-slate-600 bg-slate-700 text-white hover:bg-blue-600 hover:border-blue-500 active:scale-95'
                )}
              >
                {w.russe}
              </button>
            );
          })}
        </div>
        <div className="flex flex-col gap-2">
          {data.right.map((w) => {
            const done = matched.includes(w.id);
            const bad = wrongRight === w.id;
            return (
              <button
                key={w.id}
                onClick={() => tapRight(w.id)}
                disabled={done}
                className={cell(
                  done
                    ? 'border-green-500 bg-green-900/30 text-green-300'
                    : bad
                    ? 'border-red-500 bg-red-900/40 text-red-300'
                    : 'border-slate-600 bg-slate-700 text-white hover:bg-blue-600 hover:border-blue-500 active:scale-95'
                )}
              >
                {w[lang.wordField]}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Audio (existant) : quelle phrase as-tu entendue ? ────────────────────────
function AudioGame({ pool, lang, play, playAuto, onDone }) {
  const [data] = useState(() => {
    const cand = pool.filter((w) => w[lang.exampleRuField] && w[lang.exampleLangField]);
    if (cand.length < 4) return null;
    const w = shuffle(cand)[0];
    const decoys = shuffle(cand.filter((x) => x.id !== w.id))
      .slice(0, 3)
      .map((x) => x[lang.exampleLangField]);
    return { word: w, options: shuffle([w[lang.exampleLangField], ...decoys]) };
  });
  const [selected, setSelected] = useState(null);
  useSkipIfEmpty(data, onDone);
  useEffect(() => {
    if (data) playAuto(audioUrl(data.word.id, lang.audioEx)); // 1re écoute non demandée
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!data) return null;

  const correct = data.word[lang.exampleLangField];
  const choose = (opt) => {
    if (selected !== null) return;
    setSelected(opt);
    if (opt === correct) setTimeout(onDone, AUTO_MS);
  };

  return (
    <div className="flex flex-col gap-5 py-2">
      <div className="text-center">
        <button
          onClick={() => play(audioUrl(data.word.id, lang.audioEx))}
          className="w-16 h-16 rounded-full bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/40 flex items-center justify-center mx-auto transition-colors"
          title={lang.ui.listenAgain}
        >
          <svg viewBox="0 0 24 24" className="w-8 h-8 fill-blue-300">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
          </svg>
        </button>
        <p className="text-xs text-slate-500 mt-3">{lang.ui.bonusQuestion}</p>
      </div>
      <div className="flex flex-col gap-2">
        {data.options.map((opt) => {
          const state =
            selected === null ? 'idle' : opt === correct ? 'good' : opt === selected ? 'bad' : 'off';
          return (
            <button
              key={opt}
              onClick={() => choose(opt)}
              disabled={selected !== null}
              className={`${optClass(state)} text-left`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {selected !== null && selected !== correct && (
        <NextButton onDone={onDone} label={lang.ui.next} />
      )}
    </div>
  );
}

// ── Échelle de production (chantier C) : exercices CIBLÉS sur un mot précis, ────
// choisis selon la force SRS du mot (répétitions). Contrairement aux jeux bonus
// ci-dessus (qui piochent leur propre mot au hasard pour casser la monotonie),
// ceux-ci portent sur le mot courant du quiz et rapportent un résultat correct/
// faux via onDone(correct) → le SRS est mis à jour comme pour un QCM.

// Reconnaissance inversée / production-lite (force 2-3) : la traduction est
// affichée, on choisit le mot RUSSE parmi des leurres. Plus dur qu'un QCM RU→FR.
export function WordReverse({ word, pool, lang, onDone, onSettled, expired }) {
  const [data] = useState(() => {
    // Leurres filtrés PAR LE SENS : deux mots russes de même traduction
    // (« Пить » et « Выпить » pour « Boire ») donnaient deux bonnes réponses.
    const others = pickDistractors(word, pool, lang.wordField, 3).filter((w) => w.russe);
    return { options: shuffle([word, ...others]) };
  });
  const [selected, setSelected] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  // Temps écoulé : on met en évidence la bonne réponse (aucune n'est marquée fausse,
  // puisque l'utilisateur n'a rien choisi).
  useExpiry(expired, !!selected, onSettled, onDone, ADVANCE_MS_PLAIN, () => setTimedOut(true));

  // Rien à lire ici (pas de phrase), donc la durée courte. `onSettled` prévient
  // QuizGame que le feedback commence, pour que la barre du haut le montre.
  const choose = (opt) => {
    if (selected) return;
    setSelected(opt);
    const ok = opt.id === word.id;
    onSettled?.(ADVANCE_MS_PLAIN);
    setTimeout(() => onDone(ok), ADVANCE_MS_PLAIN);
  };

  return (
    <div className="flex flex-col gap-5 py-2">
      <Hint>{lang.ui.bonus.reverse}</Hint>
      <p className="text-center text-2xl md:text-3xl font-semibold text-white break-words">{word[lang.wordField]}</p>
      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt) => {
          const state = !selected && !timedOut ? 'idle'
            : opt.id === word.id ? 'good'
            : opt.id === selected?.id ? 'bad' : 'off';
          return (
            <button key={opt.id} lang="ru" onClick={() => choose(opt)} disabled={!!selected || timedOut} className={`${optClass(state)} text-center`}>
              {opt.russe}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Production (force 4+) : reconstituer le mot russe à partir de ses lettres en
// vrac. Une seule tentative jugée (réarrangement libre avant de poser la dernière
// tuile) ; en cas d'erreur on révèle l'orthographe. Meilleur ancrage que le QCM.
export function WordBuild({ word, lang, playAuto, onDone, onSettled, expired }) {
  const target = (word.russe || '').toLowerCase();
  const [tiles] = useState(() => {
    const chars = [...target].map((ch, i) => ({ id: i, ch }));
    let t, tries = 0;
    do { t = shuffle(chars); tries += 1; } while (tries < 8 && t.map((x) => x.ch).join('') === target);
    return t;
  });
  const [placed, setPlaced] = useState([]);
  const [result, setResult] = useState(null); // null | 'good' | 'bad' | 'timeout'
  const doneRef = useRef(false);
  // Temps écoulé : on montre l'orthographe et on passe, sans juger la tentative.
  useExpiry(expired, !!result, onSettled, onDone, ADVANCE_MS_PLAIN, () => {
    doneRef.current = true;
    setResult('timeout');
  });

  const tileById = (id) => tiles.find((t) => t.id === id);
  const available = tiles.filter((t) => !placed.includes(t.id));

  const finish = (ok) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setResult(ok ? 'good' : 'bad');
    if (ok) playAuto(audioUrl(word.id, 'word'));
    onSettled?.(ADVANCE_MS_PLAIN);
    setTimeout(() => onDone(ok), ADVANCE_MS_PLAIN);
  };
  const placeTile = (id) => {
    if (result || placed.includes(id)) return;
    const next = [...placed, id];
    setPlaced(next);
    if (next.length === target.length) finish(next.map((i) => tileById(i).ch).join('') === target);
  };
  const removeAt = (idx) => { if (!result) setPlaced(placed.filter((_, i) => i !== idx)); };

  const slotCls = result === 'good' ? 'border-green-500/70 bg-green-500/10 text-green-300'
    : result === 'bad' ? 'border-red-500/70 bg-red-500/10 text-red-300'
    : 'border-blue-500/50 bg-blue-500/10 text-blue-200';

  return (
    <div className="flex flex-col gap-4 py-1">
      <Hint>{lang.ui.bonus.anagram}</Hint>

      {/* Le mot à produire : c'est LA question, il prend donc la taille d'une
          question (le mot russe fait pareil sur la carte QCM). */}
      <p className="text-center text-2xl md:text-3xl font-semibold text-white break-words">
        {word[lang.wordField]}
      </p>

      {/* Le mot en construction : autant d'emplacements que de lettres, dessinés
          d'avance. On voit donc la LONGUEUR du mot et ce qu'il reste à poser —
          l'ancien « … » ne disait rien tant qu'on n'avait pas commencé. */}
      <div lang="ru" className="flex flex-wrap justify-center gap-1.5">
        {Array.from({ length: target.length }, (_, idx) => {
          const id = placed[idx];
          if (id === undefined) {
            return <span key={`slot-${idx}`} className="w-10 h-12 rounded-lg border border-dashed border-slate-700" />;
          }
          return (
            <button key={id} onClick={() => removeAt(idx)} className={`w-10 h-12 rounded-lg text-xl font-bold border ${slotCls}`}>
              {tileById(id).ch}
            </button>
          );
        })}
      </div>

      {/* Les lettres disponibles vivent dans un CASIER en retrait. Sans lui, les deux
          rangées (le mot en construction, les lettres à poser) se ressemblaient trop
          et on ne savait pas laquelle était laquelle. Hauteur réservée : sinon le
          casier se vide au fur et à mesure et tout le bloc remonte. */}
      <div lang="ru" className="flex flex-wrap justify-center gap-1.5 min-h-[4.5rem] items-center bg-slate-900/40 border border-slate-700/60 rounded-xl px-3 py-3">
        {available.map((t) => (
          <button key={t.id} onClick={() => placeTile(t.id)} disabled={!!result} className="w-10 h-12 rounded-lg text-xl font-bold border border-slate-600 bg-slate-700 text-white hover:border-blue-500 hover:bg-blue-600 active:scale-95 transition-colors">
            {t.ch}
          </button>
        ))}
      </div>

      <p lang="ru" className="text-center h-6 text-green-400 font-bold break-all">
        {(result === 'bad' || result === 'timeout') ? word.russe : ''}
      </p>
    </div>
  );
}

// ÉCOUTE (modalité « entendre ») : on n'entend que le mot, prononcé, et il faut
// retrouver son sens. Rien n'est écrit en russe — c'est la seule modalité qui
// travaille la reconnaissance à l'oreille, celle dont on a besoin pour comprendre
// quelqu'un qui parle. Ciblée sur le mot courant, elle rapporte correct/faux comme
// les autres. La lecture part toute seule à l'affichage, et le gros bouton permet
// de réécouter autant qu'on veut.
export function WordListen({ word, pool, lang, play, playAuto, onDone, onSettled, expired }) {
  const [data] = useState(() => {
    const others = pickDistractors(word, pool, lang.wordField, 3);
    return { options: shuffle([word, ...others]) };
  });
  const [selected, setSelected] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  useExpiry(expired, !!selected, onSettled, onDone, ADVANCE_MS_PLAIN, () => setTimedOut(true));

  useEffect(() => {
    playAuto(audioUrl(word.id, 'word'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revealed = !!selected || timedOut;
  const choose = (opt) => {
    if (revealed) return;
    setSelected(opt);
    onSettled?.(ADVANCE_MS_PLAIN);
    setTimeout(() => onDone(opt.id === word.id), ADVANCE_MS_PLAIN);
  };

  return (
    <div className="flex flex-col gap-4 py-1">
      <Hint>{lang.ui.bonus.listen}</Hint>

      <div className="flex justify-center">
        <button
          onClick={() => play(audioUrl(word.id, 'word'))}
          className="w-16 h-16 rounded-full bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/40 flex items-center justify-center transition-colors"
          title={lang.ui.listen}
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-blue-300">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
          </svg>
        </button>
      </div>

      {/* Le mot écrit n'apparaît qu'une fois répondu : avant, il donnerait la
          réponse à qui sait lire le russe, et l'exercice ne testerait plus l'oreille. */}
      <p lang="ru" className="text-center h-8 text-2xl font-bold text-blue-300 break-all">
        {revealed ? word.russe : ''}
      </p>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt) => {
          const state = !revealed ? 'idle'
            : opt.id === word.id ? 'good'
            : opt.id === selected?.id ? 'bad' : 'off';
          return (
            <button key={opt.id} onClick={() => choose(opt)} disabled={revealed} className={`${optClass(state)} text-center`}>
              {opt[lang.wordField]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Compréhension EN CONTEXTE (modalité « comprendre », chantier v2) : la phrase
// d'exemple du mot est affichée avec le mot MASQUÉ ; on le retrouve par le sens.
// Données déjà en base (example_ru + audio par mot) → scalable, contextuel.
// Ciblé sur le mot courant, rapporte correct/faux via onDone (SRS mis à jour).
export function WordCloze({ word, pool, lang, play, playAuto, onDone, onSettled, expired }) {
  const [data] = useState(() => {
    const sentence = word[lang.exampleRuField];
    if (!sentence || !word.russe) return null;
    const ranges = findHighlightRanges(sentence, word.russe);
    if (!ranges.length) return null;
    const [start, end] = ranges[0];
    // Leurres d'un AUTRE champ lexical (voir clozeDistractors) : un synonyme, ou
    // simplement un mot de la même famille, comblerait le trou tout aussi bien.
    const distractors = clozeDistractors(word, pool, lang.wordField, 3).filter((x) => x.russe);
    if (distractors.length < 3) return null;
    return {
      before: sentence.slice(0, start),
      form: sentence.slice(start, end),
      after: sentence.slice(end),
      options: shuffle([word, ...distractors]),
    };
  });
  const [selected, setSelected] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  // Phrase inexploitable (rare : le mot devrait figurer dans son propre exemple) :
  // on rend la main SANS fausser le SRS (onDone(null) → pas d'enregistrement).
  useSkipIfEmpty(data, () => onDone(null));
  // Temps écoulé : le trou se remplit avec la bonne forme, traduction et audio
  // apparaissent, puis on passe. Rien n'est enregistré.
  useExpiry(expired, !!selected, onSettled, onDone, ADVANCE_MS_EXAMPLE, () => {
    setTimedOut(true);
    playAuto?.(audioUrl(word.id, lang.audioEx));
  });
  if (!data) return null;

  const revealed = !!selected || timedOut;
  const solved = (selected && selected.id === word.id) || timedOut;
  // La réponse dévoile la phrase complète, sa traduction et son audio : c'est la
  // durée longue, sinon tout cela disparaissait en 700 ms sur un succès.
  const choose = (opt) => {
    if (selected) return;
    setSelected(opt);
    const ok = opt.id === word.id;
    // La phrase, trou comblé, se prononce : c'est ici qu'on entend enfin le mot
    // à sa place dans une vraie phrase (et fléchi). Même silence corrigé que dans
    // le panneau du QCM.
    playAuto?.(audioUrl(word.id, lang.audioEx));
    onSettled?.(ADVANCE_MS_EXAMPLE);
    setTimeout(() => onDone(ok), ADVANCE_MS_EXAMPLE);
  };

  return (
    <div className="flex flex-col gap-5 py-2">
      <Hint>{lang.ui.bonus.cloze}</Hint>
      {/* Encart en RETRAIT (plus sombre que la carte), comme le panneau de révélation
          du QCM : sur un fond slate-800, un encart plus clair remonte au premier plan
          alors qu'il doit se lire comme un creux. */}
      <div className="bg-slate-900/40 border border-slate-700/60 rounded-xl px-4 py-4 text-center">
        <p lang="ru" className="text-base md:text-lg text-blue-100 leading-relaxed break-words">
          {data.before}
          {revealed ? (
            <strong className={solved ? 'text-green-300 font-bold' : 'text-red-300 font-bold'}>{data.form}</strong>
          ) : (
            <span className="text-blue-300 font-semibold tracking-widest">·····</span>
          )}
          {data.after}
        </p>
        {/* Traduction + écoute n'apparaissent qu'après la réponse : leur place est
            réservée d'avance, sinon l'encart grandissait de 54 px pile au moment où
            l'on doit lire, et poussait les options vers le bas. */}
        <div className="min-h-[3.25rem] flex flex-col justify-center">
          {revealed && word[lang.exampleLangField] && (
            <p className="text-xs text-slate-400 mt-2 break-words">{word[lang.exampleLangField]}</p>
          )}
          {revealed && (
            <div className="mt-2 flex justify-center">
              <PlayButton onClick={() => play(audioUrl(word.id, lang.audioEx))} title={lang.ui.listen} />
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt) => {
          const state = !revealed ? 'idle'
            : opt.id === word.id ? 'good'
            : opt.id === selected?.id ? 'bad' : 'off';
          return (
            <button key={opt.id} lang="ru" onClick={() => choose(opt)} disabled={revealed} className={`${optClass(state)} text-center`}>
              {opt.russe}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Choisit un type de bonus jouable avec le pool courant, en évitant de répéter
// le précédent. Renvoie null si aucun n'est jouable. NB : reverse et anagram ne
// sont plus ici (ils sont devenus des exercices principaux, pilotés par la force).
export function pickBonusType(pool, lang, soundOn, lastType) {
  const withTrans = pool.filter((w) => w.russe && w[lang.wordField]);
  const withEx = pool.filter((w) => w[lang.exampleRuField]);
  const types = [];
  if (withEx.length >= 1 && withTrans.length >= 4) types.push('cloze');
  if (withTrans.length >= 4) types.push('match');
  if (soundOn && pool.filter((w) => w[lang.exampleRuField] && w[lang.exampleLangField]).length >= 4) {
    types.push('audio');
  }
  if (!types.length) return null;
  const pickable = types.length > 1 ? types.filter((t) => t !== lastType) : types;
  const choices = pickable.length ? pickable : types;
  return choices[Math.floor(Math.random() * choices.length)];
}

export default function BonusGame({ type, pool, lang, play, playAuto, onDone }) {
  switch (type) {
    case 'cloze':
      return <ClozeGame pool={pool} lang={lang} play={play} onDone={onDone} />;
    case 'match':
      return <MatchGame pool={pool} lang={lang} onDone={onDone} />;
    case 'anagram':
      return <AnagramGame pool={pool} lang={lang} play={play} playAuto={playAuto} onDone={onDone} />;
    case 'reverse':
      return <ReverseGame pool={pool} lang={lang} onDone={onDone} />;
    case 'audio':
      return <AudioGame pool={pool} lang={lang} play={play} playAuto={playAuto} onDone={onDone} />;
    default:
      return null;
  }
}
